import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { cn } from "../lib/utils";
import {
  ArrowLeft,
  DollarSign,
  Calendar,
  Save,
  Trash2,
  CheckCircle2,
  Clock,
  Loader2,
} from "lucide-react";
import { toast } from "sonner";

const TrpPaymentDetailPage = () => {
  const { projectId, paymentIndex } = useParams();
  const navigate = useNavigate();
  
  const [project, setProject] = useState(null);
  const [payment, setPayment] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  // Form state
  const [formData, setFormData] = useState({
    description: "",
    amount: 0,
    planned_date: "",
    status: "pending",
    notes: "",
  });

  useEffect(() => {
    fetchData();
  }, [projectId, paymentIndex]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const res = await api.get(`/trp/projects/${projectId}`);
      const projectData = res.data;
      setProject(projectData);
      
      const paymentSchedule = projectData.payment_schedule || [];
      const idx = parseInt(paymentIndex);
      
      if (idx >= 0 && idx < paymentSchedule.length) {
        const p = paymentSchedule[idx];
        setPayment(p);
        setFormData({
          description: p.description || p.notes || `Payment ${idx + 1}`,
          amount: p.amount || 0,
          planned_date: p.planned_date || "",
          status: p.status || "pending",
          notes: p.notes || "",
        });
      }
    } catch (error) {
      toast.error("Failed to load payment");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const paymentSchedule = [...(project.payment_schedule || [])];
      const idx = parseInt(paymentIndex);
      
      paymentSchedule[idx] = {
        ...paymentSchedule[idx],
        description: formData.description,
        amount: parseFloat(formData.amount) || 0,
        planned_date: formData.planned_date,
        status: formData.status,
        notes: formData.notes,
      };
      
      // Recalculate totals
      const totalPaid = paymentSchedule
        .filter(p => p.status === "paid")
        .reduce((sum, p) => sum + (p.amount || 0), 0);
      
      await api.put(`/trp/projects/${projectId}`, {
        payment_schedule: paymentSchedule,
        total_paid: totalPaid,
        total_pending: (project.total_amount || 0) - totalPaid,
      });
      
      toast.success("Payment updated");
      fetchData();
    } catch (error) {
      toast.error("Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm("Delete this payment?")) return;
    
    try {
      const paymentSchedule = [...(project.payment_schedule || [])];
      const idx = parseInt(paymentIndex);
      paymentSchedule.splice(idx, 1);
      
      // Recalculate totals
      const totalPaid = paymentSchedule
        .filter(p => p.status === "paid")
        .reduce((sum, p) => sum + (p.amount || 0), 0);
      const totalPending = paymentSchedule
        .filter(p => p.status !== "paid")
        .reduce((sum, p) => sum + (p.amount || 0), 0);
      
      await api.put(`/trp/projects/${projectId}`, {
        payment_schedule: paymentSchedule,
        total_paid: totalPaid,
        total_pending: totalPending,
      });
      
      toast.success("Payment deleted");
      navigate(`/trp/project/${projectId}`);
    } catch (error) {
      toast.error("Failed to delete");
    }
  };

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-orange-500" />
      </div>
    );
  }

  return (
    <div className="p-6 max-w-2xl mx-auto">
      {/* Back Button */}
      <Button
        variant="ghost"
        onClick={() => navigate(`/trp/project/${projectId}`)}
        className="mb-4 text-zinc-400 hover:text-zinc-50"
      >
        <ArrowLeft className="h-4 w-4 mr-2" />
        Back to Project
      </Button>

      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-zinc-50 flex items-center gap-2">
          <DollarSign className="h-6 w-6 text-green-500" />
          Payment Details
        </h1>
        <p className="text-sm text-zinc-400 mt-1">
          {project?.name} • Payment #{parseInt(paymentIndex) + 1}
        </p>
      </div>

      {/* Payment Form */}
      <Card className="bg-zinc-900/50 border-zinc-800">
        <CardHeader>
          <CardTitle className="text-lg text-zinc-50">Edit Payment</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Description */}
          <div className="space-y-2">
            <Label className="text-zinc-400">Description</Label>
            <Input
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="Payment description"
              className="bg-zinc-800 border-zinc-700 text-zinc-50"
            />
          </div>

          {/* Amount & Date Row */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="text-zinc-400 flex items-center gap-1">
                <DollarSign className="h-3 w-3" />
                Amount ($)
              </Label>
              <Input
                type="number"
                min="0"
                value={formData.amount}
                onChange={(e) => setFormData({ ...formData, amount: parseFloat(e.target.value) || 0 })}
                className="bg-zinc-800 border-zinc-700 text-zinc-50"
              />
            </div>
            
            <div className="space-y-2">
              <Label className="text-zinc-400 flex items-center gap-1">
                <Calendar className="h-3 w-3" />
                Planned Date
              </Label>
              <Input
                type="date"
                value={formData.planned_date}
                onChange={(e) => setFormData({ ...formData, planned_date: e.target.value })}
                className="bg-zinc-800 border-zinc-700 text-zinc-50"
              />
            </div>
          </div>

          {/* Status */}
          <div className="space-y-2">
            <Label className="text-zinc-400">Status</Label>
            <Select value={formData.status} onValueChange={(v) => setFormData({ ...formData, status: v })}>
              <SelectTrigger className="bg-zinc-800 border-zinc-700 text-zinc-50">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-zinc-800 border-zinc-700">
                <SelectItem value="pending" className="text-zinc-50">
                  <div className="flex items-center gap-2">
                    <Clock className="h-3 w-3 text-orange-400" />
                    Pending
                  </div>
                </SelectItem>
                <SelectItem value="paid" className="text-zinc-50">
                  <div className="flex items-center gap-2">
                    <CheckCircle2 className="h-3 w-3 text-emerald-400" />
                    Paid
                  </div>
                </SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Notes */}
          <div className="space-y-2">
            <Label className="text-zinc-400">Notes</Label>
            <Textarea
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              placeholder="Additional notes..."
              className="bg-zinc-800 border-zinc-700 text-zinc-50 min-h-[80px]"
            />
          </div>

          {/* Actions */}
          <div className="flex items-center justify-between pt-4 border-t border-zinc-800">
            <Button
              variant="destructive"
              onClick={handleDelete}
              className="bg-red-500/20 hover:bg-red-500/30 text-red-400"
            >
              <Trash2 className="h-4 w-4 mr-2" />
              Delete Payment
            </Button>
            
            <Button
              onClick={handleSave}
              disabled={saving}
              className="bg-orange-600 hover:bg-orange-700"
            >
              {saving ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Save className="h-4 w-4 mr-2" />
              )}
              Save Changes
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default TrpPaymentDetailPage;
